import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { checkUserAuthorization, updateUserLastLogin } from '../firebase/auth';
import { User } from '../types';
import { logger } from '../utils/logger';

interface UserContextType {
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  isManagement: boolean;
  isHotelStaff: boolean;
  assignedHotelIds: string[];
  isMimicking: boolean;
  mimickedUser: User | null;
  login: (userData: User) => void;
  logout: () => void;
  hasAccessToHotel: (hotelId: string) => boolean;
  startMimicking: (user: User) => void;
  stopMimicking: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mimickedUser, setMimickedUser] = useState<User | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!isMounted) return;
      
      if (firebaseUser && firebaseUser.email) {
        try {
          const { authorized, user: userData, error } = await checkUserAuthorization(firebaseUser.email);
          
          if (!isMounted) return; // Check again after async operation
          
          if (authorized && userData) {
            // Update last login time
            if (firebaseUser.email) {
              await updateUserLastLogin(firebaseUser.email);
            }
            
            if (isMounted) {
              setUser(userData);
            }
          } else {
            if (isMounted) {
              setUser(null);
              logger.error('User not authorized:', error);
            }
          }
        } catch (err) {
          if (isMounted) {
            logger.error('Error checking user authorization:', err);
            setUser(null);
          }
        }
      } else {
        if (isMounted) {
          setUser(null);
        }
      }
      
      if (isMounted) {
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const login = (userData: User) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await auth.signOut();
      setUser(null);
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  };

  // Use mimicked user if in mimic mode, otherwise use actual user
  const effectiveUser = mimickedUser || user;
  
  const isAdmin = effectiveUser?.role === 'admin';
  const isManagement = effectiveUser?.role === 'management';
  const isHotelStaff = effectiveUser?.role === 'hotel_staff';
  const assignedHotelIds = effectiveUser?.assignedHotelIds || [];
  const isMimicking = mimickedUser !== null;

  const hasAccessToHotel = (hotelId: string): boolean => {
    if (isAdmin || isManagement) return true;
    if (isHotelStaff) {
      return assignedHotelIds.includes(hotelId);
    }
    return false;
  };

  const startMimicking = (userToMimic: User) => {
    setMimickedUser(userToMimic);
  };

  const stopMimicking = () => {
    setMimickedUser(null);
  };

  const value: UserContextType = {
    user: effectiveUser,
    isLoading,
    isAdmin,
    isManagement,
    isHotelStaff,
    assignedHotelIds,
    isMimicking,
    mimickedUser,
    login,
    logout,
    hasAccessToHotel,
    startMimicking,
    stopMimicking,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
